// frontend/src/components/TrafficIntelligenceCard.jsx
// Project:      Vision ANPR by Castle Traffic Ltd - Automatic Number Plate Recognition
// Author:       Leon du Plooy - Castle Traffic Ltd - Copyright 2026
// Contact:      dev@castle-traffic.co.uk
// Created:      2026-03-09
// Updated:      2026-09-16
//
// Description:  Reusable traffic intelligence card for location-aware UI surfaces.
//               Fetches tenant-safe live traffic intelligence from the backend using
//               lat/lon and renders an operational-impact summary rather than raw telemetry.
//               Intended for Job edit/create flows and dashboard/widget reuse.
//
// Notes:
//               - Incident-first UX: surfaces the most operationally relevant live incident
//                 before lower-level flow telemetry.
//               - Job-type aware messaging comes from backend assessment.
//               - Distances/delays are rendered using backend-friendly display text
//                 (mi when traffic settings are MPH, km otherwise).
//               - Compact, mobile-friendly, no bulky nested panels.
//
// Responsive refinement (2026-09-16):
//               - Mobile removes card-inside-card framing and presents one flat intelligence surface.
//               - Desktop retains the existing bordered outer card and nested incident panel styling.
//               - Mobile metrics remain discrete compact blocks for scanability.
//               - Traffic API, debounce, assessment mapping and refresh behaviour are unchanged.

import React, { useEffect, useMemo, useRef, useState } from "react";
import axiosInstance from "../api/axiosInstance";

const isFiniteNumber = (v) => {
  const n = Number(v);
  return Number.isFinite(n);
};

const safeText = (v, fallback = "—") => {
  if (v === null || v === undefined) return fallback;
  const s = String(v).trim();
  return s || fallback;
};

const formatDateTimeGB = (value) => {
  if (!value) return "—";
  try {
    const d = new Date(value);
    if (Number.isNaN(d.getTime())) return "—";
    return d.toLocaleString("en-GB", {
      day: "2-digit",
      month: "short",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      timeZone: "Europe/London",
    });
  } catch {
    return "—";
  }
};

const chipStyle = (tone) => ({
  display: "inline-flex",
  alignItems: "center",
  gap: 8,
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 700,
  background: tone?.bg || "rgba(107, 114, 128, 0.10)",
  border: `1px solid ${tone?.border || "rgba(107, 114, 128, 0.25)"}`,
  color: tone?.text || "#4b5563",
});

const secondaryChipStyle = {
  display: "inline-flex",
  alignItems: "center",
  padding: "4px 10px",
  borderRadius: 9999,
  fontSize: 12,
  fontWeight: 700,
  background: "rgba(17, 24, 39, 0.05)",
  border: "1px solid rgba(17, 24, 39, 0.10)",
  color: "#111827",
};

const incidentIconTone = {
  critical: {
    bg: "rgba(239, 68, 68, 0.12)",
    border: "rgba(239, 68, 68, 0.28)",
    fg: "#b91c1c",
  },
  warning: {
    bg: "rgba(245, 158, 11, 0.12)",
    border: "rgba(245, 158, 11, 0.28)",
    fg: "#b45309",
  },
  monitor: {
    bg: "rgba(59, 130, 246, 0.10)",
    border: "rgba(59, 130, 246, 0.24)",
    fg: "#1d4ed8",
  },
  neutral: {
    bg: "rgba(107, 114, 128, 0.10)",
    border: "rgba(107, 114, 128, 0.22)",
    fg: "#4b5563",
  },
};

const dedupeStrings = (arr) => {
  if (!Array.isArray(arr)) return [];
  const seen = new Set();
  return arr.filter((item) => {
    const key = String(item || "").trim().toLowerCase();
    if (!key || seen.has(key)) return false;
    seen.add(key);
    return true;
  });
};

const getPrimaryIncident = (data) => {
  return data?.assessment?.primary_incident || (Array.isArray(data?.incidents) && data.incidents.length ? data.incidents[0] : null);
};

const getIncidentEventSet = (incident) => {
  return new Set(
    dedupeStrings(incident?.event_descriptions || []).map((x) => String(x).trim().toLowerCase())
  );
};

const mapIncidentVisual = (incident, assessmentStatus = "unknown") => {
  const events = getIncidentEventSet(incident);

  if (events.has("closed")) {
    return {
      key: "closed",
      label: "Road closed",
      tone: incidentIconTone.critical,
    };
  }

  if (events.has("lane closed")) {
    return {
      key: "lane_closed",
      label: "Lane restriction",
      tone: incidentIconTone.warning,
    };
  }

  if (events.has("accident") || events.has("collision")) {
    return {
      key: "collision",
      label: "Collision",
      tone: incidentIconTone.warning,
    };
  }

  if (events.has("broken down vehicle")) {
    return {
      key: "breakdown",
      label: "Broken down vehicle",
      tone: incidentIconTone.warning,
    };
  }

  if (events.has("stationary traffic")) {
    return {
      key: "stationary",
      label: "Stationary traffic",
      tone: incidentIconTone.warning,
    };
  }

  if (events.has("queuing traffic")) {
    return {
      key: "queue",
      label: "Queuing traffic",
      tone: incidentIconTone.monitor,
    };
  }

  if (assessmentStatus === "critical") {
    return {
      key: "warning",
      label: "Critical incident",
      tone: incidentIconTone.critical,
    };
  }

  if (assessmentStatus === "warning") {
    return {
      key: "warning",
      label: "Traffic incident",
      tone: incidentIconTone.warning,
    };
  }

  if (assessmentStatus === "monitor") {
    return {
      key: "monitor",
      label: "Traffic activity",
      tone: incidentIconTone.monitor,
    };
  }

  return {
    key: "traffic",
    label: "Traffic intelligence",
    tone: incidentIconTone.neutral,
  };
};

const IncidentSvgIcon = ({ type = "traffic", fg = "#4b5563", size = 30 }) => {
  const common = {
    width: size,
    height: size,
    viewBox: "0 0 32 32",
    fill: "none",
    xmlns: "http://www.w3.org/2000/svg",
    "aria-hidden": true,
  };

  if (type === "closed") {
    return (
      <svg {...common}>
        <rect x="4" y="9" width="24" height="14" rx="3" stroke={fg} strokeWidth="2" />
        <path d="M10 14L22 18" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
        <path d="M22 14L10 18" stroke={fg} strokeWidth="2.5" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "lane_closed") {
    return (
      <svg {...common}>
        <path d="M8 25L12 7" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M20 25L24 7" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M15.5 24L18.5 8" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
        <path d="M22.5 10L27 14.5L22.5 19" stroke={fg} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "collision") {
    return (
      <svg {...common}>
        <rect x="5.5" y="16" width="8.5" height="5" rx="1.2" stroke={fg} strokeWidth="2" />
        <rect x="18" y="11" width="8.5" height="5" rx="1.2" stroke={fg} strokeWidth="2" />
        <path d="M14 18L18 15" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M13.5 12.5L17.5 19.5" stroke={fg} strokeWidth="2" strokeLinecap="round" />
      </svg>
    );
  }

  if (type === "breakdown") {
    return (
      <svg {...common}>
        <path d="M6 18.5H23L20.5 12.5H10L8.5 15.5H6V18.5Z" stroke={fg} strokeWidth="2" strokeLinejoin="round" />
        <circle cx="10" cy="21.5" r="2" stroke={fg} strokeWidth="2" />
        <circle cx="20" cy="21.5" r="2" stroke={fg} strokeWidth="2" />
        <path d="M25 8L28 11L25 14" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "stationary") {
    return (
      <svg {...common}>
        <path d="M8 24L12 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M20 24L24 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M15.5 23L18.5 9" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
        <path d="M10 6V12" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M7.5 9.5L10 12L12.5 9.5" stroke={fg} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "queue") {
    return (
      <svg {...common}>
        <path d="M8 24L12 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M20 24L24 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M15.5 23L18.5 9" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
        <path d="M9.5 11H22.5" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M19 8L22.5 11L19 14" stroke={fg} strokeWidth="2.25" strokeLinecap="round" strokeLinejoin="round" />
      </svg>
    );
  }

  if (type === "warning") {
    return (
      <svg {...common}>
        <path d="M16 6L27 25H5L16 6Z" stroke={fg} strokeWidth="2" strokeLinejoin="round" />
        <path d="M16 12V18" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <circle cx="16" cy="22" r="1.25" fill={fg} />
      </svg>
    );
  }

  if (type === "monitor") {
    return (
      <svg {...common}>
        <path d="M8 24L12 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M20 24L24 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
        <path d="M15.5 23L18.5 9" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
        <circle cx="16" cy="16" r="4.5" stroke={fg} strokeWidth="2" />
      </svg>
    );
  }

  return (
    <svg {...common}>
      <path d="M8 24L12 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
      <path d="M20 24L24 8" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
      <path d="M15.5 23L18.5 9" stroke={fg} strokeWidth="2" strokeLinecap="round" strokeDasharray="2.5 3" />
      <path d="M9 17H23" stroke={fg} strokeWidth="2.25" strokeLinecap="round" />
    </svg>
  );
};

const metricBlockStyle = {
  minWidth: 0,
  border: "1px solid #e5e7eb",
  borderRadius: 10,
  padding: "10px 12px",
  background: "#ffffff",
};

const TrafficIntelligenceCard = ({
  title = "Traffic Intelligence",
  lat,
  lon,
  locationLabel = "",
  jobTypeLabel = "",
  persist = true,
  className = "",
  showRefresh = true,
  autoFetch = true,
  showTechnicalFooter = false,
}) => {
  const [loading, setLoading] = useState(false);
  const [data, setData] = useState(null);
  const [err, setErr] = useState("");
  const debounceRef = useRef(null);
  const requestKeyRef = useRef("");

  const hasCoords = isFiniteNumber(lat) && isFiniteNumber(lon);
  const numericLat = hasCoords ? Number(lat) : null;
  const numericLon = hasCoords ? Number(lon) : null;

  const loadTraffic = async () => {
    if (!hasCoords) {
      setData(null);
      setErr("");
      setLoading(false);
      return;
    }

    const requestKey = `${numericLat}|${numericLon}|${persist ? "1" : "0"}|${jobTypeLabel || ""}`;
    requestKeyRef.current = requestKey;

    setLoading(true);
    setErr("");

    try {
      const res = await axiosInstance.get("traffic/intelligence", {
        params: {
          lat: numericLat,
          lon: numericLon,
          persist: persist ? "true" : "false",
          cache_bust: "true",
          job_type_label: jobTypeLabel || undefined,
        },
      });

      if (requestKeyRef.current !== requestKey) return;
      setData(res?.data || null);
    } catch (e) {
      if (requestKeyRef.current !== requestKey) return;
      const msg =
        e?.response?.data?.detail ||
        e?.response?.data?.message ||
        e?.message ||
        "Failed to load traffic intelligence.";
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      setData(null);
    } finally {
      if (requestKeyRef.current === requestKey) {
        setLoading(false);
      }
    }
  };

  useEffect(() => {
    if (!autoFetch) return undefined;

    if (debounceRef.current) clearTimeout(debounceRef.current);

    if (!hasCoords) {
      setData(null);
      setErr("");
      setLoading(false);
      return undefined;
    }

    debounceRef.current = setTimeout(() => {
      loadTraffic();
    }, 450);

    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoFetch, numericLat, numericLon, persist, jobTypeLabel]);

  const assessment = data?.assessment || null;
  const traffic = data?.traffic || null;
  const incidents = Array.isArray(data?.incidents) ? data.incidents : [];
  const primaryIncident = useMemo(() => getPrimaryIncident(data), [data]);
  const primaryVisual = useMemo(
    () => mapIncidentVisual(primaryIncident, assessment?.status),
    [primaryIncident, assessment?.status]
  );

  const surfacedCount = incidents.length;
  const totalCount = Number.isFinite(Number(data?.incident_total_count))
    ? Number(data.incident_total_count)
    : surfacedCount;

  const roadLabel = safeText(
    traffic?.road_name || traffic?.road_display || traffic?.road_class_label || traffic?.road_class,
    "Nearest road segment"
  );

  const speedNow = isFiniteNumber(traffic?.current_speed) ? `${Math.round(Number(traffic.current_speed))}` : "—";
  const speedFree = isFiniteNumber(traffic?.free_flow_speed) ? `${Math.round(Number(traffic.free_flow_speed))}` : "—";
  const flowDelay = safeText(traffic?.delay_display_text, "—");
  const flowRatio = isFiniteNumber(traffic?.congestion_ratio)
    ? `${Math.round(Number(traffic.congestion_ratio) * 100)}%`
    : "—";

  return (
    <div
      className={`mt-3 min-w-0 bg-transparent sm:mt-4 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-white ${className}`}
    >
      <div className="flex min-w-0 flex-wrap items-start justify-between gap-3 px-0 py-2 sm:px-4 sm:py-3">
        <div className="min-w-0 flex-1">
          <div className="text-sm font-semibold" style={{ color: "var(--accent-color)" }}>
            {title}
          </div>
          <div className="break-words text-xs text-gray-600">
            {hasCoords
              ? `Operational traffic impact for pinned location${locationLabel ? ` · ${locationLabel}` : ""}${
                  jobTypeLabel ? ` · ${jobTypeLabel}` : ""
                }`
              : "Set a valid pinned location to enable traffic intelligence."}
          </div>
        </div>

        <div className="flex min-w-0 flex-wrap items-center gap-2">
          {hasCoords && assessment ? (
            <span style={chipStyle(assessment?.tone)}>{safeText(assessment?.tone?.label, "Traffic status")}</span>
          ) : (
            <span className="inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
              No intelligence
            </span>
          )}

          {showRefresh && hasCoords && (
            <button
              type="button"
              onClick={loadTraffic}
              disabled={loading}
              className="bg-btnGeneralNormal hover:bg-btnGeneralHover active:bg-btnGeneralClick disabled:opacity-60 text-white text-xs font-semibold px-3 py-1 rounded shadow"
              title="Refresh traffic intelligence"
            >
              {loading ? "Loading…" : "Refresh"}
            </button>
          )}
        </div>
      </div>

      <div className="min-w-0 px-0 pb-2 sm:px-4 sm:pb-4">
        {!hasCoords ? (
          <div className="text-xs text-gray-600 italic">No valid coordinates available yet.</div>
        ) : loading && !data ? (
          <div className="text-xs text-gray-600 italic">Loading traffic intelligence…</div>
        ) : err ? (
          <div className="text-xs text-red-600">❌ {err}</div>
        ) : !data?.ok || !assessment ? (
          <div className="text-xs text-gray-600 italic">No traffic intelligence available for this location.</div>
        ) : (
          <div className="min-w-0 bg-transparent py-2 sm:rounded-lg sm:border sm:border-gray-100 sm:bg-white sm:p-4">
            <div className="flex min-w-0 items-start gap-3">
              <div
                style={{
                  width: 54,
                  height: 54,
                  borderRadius: 12,
                  background: primaryVisual.tone.bg,
                  border: `1px solid ${primaryVisual.tone.border}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  flexShrink: 0,
                }}
              >
                <IncidentSvgIcon type={primaryVisual.key} fg={primaryVisual.tone.fg} size={30} />
              </div>

              <div className="min-w-0 flex-1">
                <div
                  style={{
                    fontSize: 13,
                    lineHeight: 1.45,
                    fontWeight: 600,
                    color: "#111827",
                  }}
                >
                  {safeText(assessment?.headline, "No traffic impact summary available.")}
                </div>

                <div className="mt-2 flex flex-wrap items-center gap-2">
                  <span style={chipStyle(assessment?.tone)}>{safeText(assessment?.tone?.label, "Traffic status")}</span>

                  {primaryIncident ? (
                    <span style={secondaryChipStyle}>{primaryVisual.label}</span>
                  ) : (
                    <span style={secondaryChipStyle}>No surfaced incident</span>
                  )}

                  <span style={secondaryChipStyle}>
                    {surfacedCount} shown{totalCount > surfacedCount ? ` of ${totalCount}` : ""}
                  </span>
                </div>
              </div>
            </div>

            {primaryIncident ? (
              <div className="mt-4 border-t border-gray-200 pt-3 sm:rounded-xl sm:border sm:border-gray-200 sm:bg-[#fafafa] sm:p-[14px]">
                <div className="flex items-start justify-between gap-3 flex-wrap">
                  <div className="min-w-0 flex-1">
                    <div className="text-xs font-semibold uppercase tracking-wide text-gray-500">
                      Primary live incident
                    </div>
                    <div className="mt-1 break-words text-sm font-semibold leading-5 text-gray-900">
                      {safeText(primaryIncident?.from)} → {safeText(primaryIncident?.to)}
                    </div>
                    <div className="mt-1 break-words text-xs leading-5 text-gray-600">
                      {dedupeStrings(primaryIncident?.event_descriptions).join(" · ") || "Incident reported"}
                    </div>
                  </div>

                  <div className="flex min-w-0 flex-wrap items-center gap-2">
                    {primaryIncident?.flow_distance_display_text ? (
                      <span style={secondaryChipStyle}>
                        Corridor {safeText(primaryIncident.flow_distance_display_text)}
                      </span>
                    ) : null}

                    {primaryIncident?.distance_display_text ? (
                      <span style={secondaryChipStyle}>
                        Pin {safeText(primaryIncident.distance_display_text)}
                      </span>
                    ) : null}

                    {primaryIncident?.delay_display_text ? (
                      <span style={secondaryChipStyle}>
                        Delay {safeText(primaryIncident.delay_display_text)}
                      </span>
                    ) : null}
                  </div>
                </div>

                {Array.isArray(primaryIncident?.road_numbers) && primaryIncident.road_numbers.length > 0 ? (
                  <div className="mt-2 text-xs text-gray-600">
                    Roads: {primaryIncident.road_numbers.join(", ")}
                  </div>
                ) : null}
              </div>
            ) : null}

            <div className="mt-4 grid min-w-0 grid-cols-2 gap-2 sm:flex sm:flex-wrap sm:gap-3">
              <div className="min-w-0 sm:min-w-[110px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Road</div>
                <div className="mt-1 break-words text-sm font-semibold leading-5 text-gray-900">{roadLabel}</div>
              </div>

              <div className="min-w-0 sm:min-w-[110px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Current speed</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{speedNow}</div>
              </div>

              <div className="min-w-0 sm:min-w-[110px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Free flow</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{speedFree}</div>
              </div>

              <div className="min-w-0 sm:min-w-[110px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Travel delay</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{flowDelay}</div>
              </div>

              <div className="min-w-0 sm:min-w-[110px]" style={metricBlockStyle}>
                <div className="text-[11px] uppercase tracking-wide text-gray-500">Flow ratio</div>
                <div className="mt-1 text-sm font-semibold text-gray-900">{flowRatio}</div>
              </div>
            </div>

            <div className="mt-4 space-y-1.5">
              {Array.isArray(assessment?.reasons) && assessment.reasons.length > 0 ? (
                assessment.reasons.map((reason, idx) => (
                  <div key={idx} className="break-words text-xs leading-5 text-gray-700">
                    • {reason}
                  </div>
                ))
              ) : (
                <div className="break-words text-xs leading-5 text-gray-700">
                  • No operationally relevant live traffic signal identified for this job type at the pinned location.
                </div>
              )}
            </div>

            {showTechnicalFooter ? (
              <div className="mt-3 break-words text-[11px] leading-5 text-gray-500">
                Generated {formatDateTimeGB(data?.generated_at)}
                {data?.settings_source ? ` · Source ${safeText(data.settings_source)}` : ""}
                {Number.isFinite(Number(assessment?.incident_search_radius_km))
                  ? ` · Search radius ${Number(assessment.incident_search_radius_km).toFixed(2)} km`
                  : ""}
                {traffic?.road_class_label ? ` · Class ${safeText(traffic.road_class_label)}` : ""}
              </div>
            ) : null}
          </div>
        )}
      </div>
    </div>
  );
};

export default TrafficIntelligenceCard;